import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  AcademicCapIcon, 
  GlobeAltIcon, 
  UsersIcon, 
  ChartBarIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  SparklesIcon,
  RocketLaunchIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import CourseCard from '../components/CourseCard';
import TestimonialCard from '../components/TestimonialCard';
import BookingButton from '../components/BookingButton';
import SEO from '../components/SEO';
import { courses } from '../data/courses';
import { testimonials } from '../data/testimonials';
import {
  organizationStructuredData,
  reviewStructuredData,
  faqStructuredData,
  localBusinessStructuredData,
  professionalServiceStructuredData
} from '../data/seo';
import { services } from '../data/services';

const Home = () => {
  const stats = [
    { label: 'Students Taught', value: '500+', icon: UsersIcon },
    { label: 'Countries Reached', value: '25+', icon: GlobeAltIcon },
    { label: 'Success Rate', value: '95%', icon: ChartBarIcon },
    { label: 'Years Experience', value: '7+', icon: AcademicCapIcon },
  ];

  const heroPoints = [
    'Live 1:1 coaching tailored to your goals',
    'Project-based lessons with real portfolio outcomes',
    'Clear roadmap from beginner to job-ready skills',
    'Flexible timezone scheduling for global students'
  ];

  const outcomes = [
    {
      title: 'Career Switch Support',
      description: 'Move from beginner or non-tech roles into practical software skills employers value.',
      icon: RocketLaunchIcon
    },
    {
      title: 'Hands-On Skill Building',
      description: 'Build real apps, debug real challenges, and learn the exact workflow professionals use.',
      icon: SparklesIcon
    },
    {
      title: 'Affordable Expert Mentorship',
      description: 'High-impact coaching designed to deliver strong results without high bootcamp costs.',
      icon: CurrencyDollarIcon
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Book Your Intro Session',
      description: 'We discuss your current level, goals, and timeline.'
    },
    {
      step: '02',
      title: 'Get a Personal Learning Plan',
      description: 'Receive a custom roadmap with clear weekly milestones.'
    },
    {
      step: '03',
      title: 'Attend Weekly 1:1 Sessions',
      description: 'Learn by building projects with feedback, support, and accountability.'
    },
    {
      step: '04',
      title: 'Track Results and Advance',
      description: 'Improve your portfolio, confidence, and interview readiness over time.'
    }
  ];

  const skillColorCards = [
    {
      skill: 'Python',
      focus: 'Automation, backend APIs, and clean coding foundations.',
      className: 'from-emerald-500 to-lime-500',
      borderClass: 'border-emerald-200 dark:border-emerald-700',
      textClass: 'text-emerald-700 dark:text-emerald-300'
    },
    {
      skill: 'React',
      focus: 'Modern frontend architecture, components, hooks, and UI patterns.',
      className: 'from-sky-500 to-cyan-500',
      borderClass: 'border-sky-200 dark:border-sky-700',
      textClass: 'text-sky-700 dark:text-sky-300'
    },
    {
      skill: 'DevOps',
      focus: 'Deployment pipelines, Docker workflows, and cloud-ready projects.',
      className: 'from-indigo-500 to-violet-600',
      borderClass: 'border-indigo-200 dark:border-indigo-700',
      textClass: 'text-indigo-700 dark:text-indigo-300'
    },
    {
      skill: 'Machine Learning',
      focus: 'Model building, evaluation, and practical ML project implementation.',
      className: 'from-fuchsia-500 to-pink-600',
      borderClass: 'border-fuchsia-200 dark:border-fuchsia-700',
      textClass: 'text-fuchsia-700 dark:text-fuchsia-300'
    },
    {
      skill: 'Data Analysis',
      focus: 'Data cleaning, insights, visualization, and decision-ready reporting.',
      className: 'from-amber-500 to-orange-500',
      borderClass: 'border-amber-200 dark:border-amber-700',
      textClass: 'text-amber-700 dark:text-amber-300'
    }
  ];

  const featuredCourses = courses.slice(0, 3);
  const featuredTestimonials = testimonials.slice(0, 3);
  const homeFaqs = [
    {
      question: 'How does a 1-on-1 python tutor session work?',
      answer: 'Each python tutor session is live and tailored to your level. We review your goals, solve coding challenges together, and assign practical exercises so your progress is measurable each week.'
    },
    {
      question: 'Can I learn with a react mentor even if I am still a beginner?',
      answer: 'Yes. As your react mentor, I can help you from fundamentals to advanced project architecture. You will build real React projects while learning modern patterns and best practices.'
    },
    {
      question: 'Do you offer devops coaching for deployment and cloud workflows?',
      answer: 'Yes. DevOps coaching includes Docker, CI/CD, cloud deployment, and production readiness. Sessions focus on practical workflows you can use in real jobs and projects.'
    },
    {
      question: 'How quickly can I see results from one-on-one coding mentorship?',
      answer: 'Most students see clear progress in the first few weeks with consistent sessions and focused practice. We use a personalized roadmap and milestone tracking to keep momentum high.'
    },
    {
      question: 'Is this suitable for career switchers and interview preparation?',
      answer: 'Absolutely. Many learners use these sessions to transition into tech, improve portfolios, and prepare for technical interviews with targeted practice and feedback.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 md:pb-0">
      <SEO
        title="1-on-1 Programming Mentor | Python, React, DevOps, ML, Data Analysis"
        description="Learn coding through premium 1-on-1 mentorship with Duke. Get personalized programming sessions in Python, React, DevOps, Machine Learning, and Data Analysis with a clear plan and real project outcomes."
        keywords="1-on-1 programming mentor, python tutor online, react coaching, devops mentor, machine learning tutor, data analysis coaching, coding interview prep, programming classes online, software engineering mentorship"
        canonical="/"
        ogImage="/og-home.jpg"
        structuredData={[
          organizationStructuredData,
          localBusinessStructuredData,
          professionalServiceStructuredData,
          reviewStructuredData(featuredTestimonials),
          faqStructuredData(homeFaqs)
        ]}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(99,102,241,0.35),_transparent_50%),radial-gradient(circle_at_20%_80%,_rgba(14,165,233,0.30),_transparent_45%)]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center rounded-full border border-cyan-300/30 bg-cyan-500/10 px-4 py-1.5 text-sm font-medium text-cyan-100 mb-6">
                <SparklesIcon className="h-4 w-4 mr-2" />
                1-on-1 Programming Mentorship That Gets Results
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Become Job-Ready Faster with
                <span className="block text-cyan-300">1-on-1 Programming Coaching</span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-slate-200 max-w-2xl">
                Stop learning alone. Get a personal mentor, a clear roadmap, and weekly live sessions
                that turn your skills into real projects, confidence, and career growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <BookingButton variant="primary" size="large" label="Book Your First Session" />
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center px-6 py-3 border-2 border-slate-300/70 text-slate-100 font-medium rounded-lg hover:bg-white hover:text-slate-900 transition-all duration-200"
                >
                  Explore Services
                  <ArrowRightIcon className="h-5 w-5 ml-2" />
                </Link>
              </div>
              <div className="flex flex-wrap gap-3">
                {skillColorCards.map((item) => (
                  <span
                    key={item.skill}
                    className="rounded-full bg-white/10 px-3 py-1 text-sm text-slate-200 border border-white/15"
                  >
                    {item.skill}
                  </span>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative lg:pl-4"
            >
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl">
                <h3 className="text-2xl font-bold mb-3">Why Students Choose TeachWithDuke</h3>
                <p className="text-slate-200 mb-6">
                  Every session is personalized for your pace, your goals, and your challenges.
                </p>
                <div className="space-y-4">
                  {heroPoints.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                      className="flex items-center"
                    >
                      <CheckCircleIcon className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
                      <span className="text-lg">{feature}</span>
                    </motion.div>
                  ))}
                </div>
                <div className="mt-8 rounded-xl bg-slate-900/40 border border-white/15 p-4">
                  <p className="text-sm text-slate-200 mb-2">Next available intro call</p>
                  <div className="flex items-center text-cyan-300 font-semibold">
                    <CalendarDaysIcon className="h-5 w-5 mr-2" />
                    Limited spots open this week
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SEO Internal Links */}
      <section className="py-10 bg-slate-100 dark:bg-slate-800/60 border-y border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-gray-700 dark:text-gray-300 text-center text-lg">
            Looking for a{' '}
            <Link to="/courses/python-programming" className="text-emerald-600 dark:text-emerald-400 font-semibold hover:underline">
              python tutor
            </Link>
            , a{' '}
            <Link to="/courses/react-development" className="text-sky-600 dark:text-sky-400 font-semibold hover:underline">
              react mentor
            </Link>
            , or{' '}
            <Link to="/courses/devops-cloud" className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline">
              devops coaching
            </Link>
            ? Explore your track and book your first live session.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 dark:bg-indigo-900 rounded-full mb-4">
                  <stat.icon className="h-8 w-8 text-indigo-600 dark:text-cyan-300" />
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Confidence Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Everything You Need to Start with Confidence</h2>
            <p className="text-xl text-slate-200 max-w-3xl mx-auto">
              Clear roadmap, practical sessions, progress tracking, and direct mentor feedback designed for serious growth.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-xl border border-white/15 bg-white/5 p-6">
              <h3 className="font-bold text-xl mb-2">Personalized Plan</h3>
              <p className="text-slate-200">Get a goal-based learning path tailored to your background, timeline, and target role.</p>
            </div>
            <div className="rounded-xl border border-white/15 bg-white/5 p-6">
              <h3 className="font-bold text-xl mb-2">Live Feedback Loop</h3>
              <p className="text-slate-200">Code reviews, debugging support, and weekly accountability to keep you moving.</p>
            </div>
            <div className="rounded-xl border border-white/15 bg-white/5 p-6">
              <h3 className="font-bold text-xl mb-2">Career-Focused Outcomes</h3>
              <p className="text-slate-200">Build portfolio-ready projects and interview-ready confidence that attracts opportunities.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Skill Color System */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Specialized Coaching by Skill
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Each track has its own learning path so you get focused, high-quality mentorship from day one.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            {skillColorCards.map((item, index) => (
              <motion.div
                key={item.skill}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className={`rounded-2xl border ${item.borderClass} bg-gray-50 dark:bg-gray-900 overflow-hidden`}
              >
                <div className={`h-2 bg-gradient-to-r ${item.className}`} />
                <div className="p-5">
                  <h3 className={`text-lg font-bold mb-2 ${item.textClass}`}>{item.skill}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{item.focus}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Built to Deliver Measurable Results
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              This is practical mentorship focused on outcomes you can see: better coding, stronger portfolio, and real career momentum.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {outcomes.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="rounded-2xl bg-white dark:bg-gray-800 p-8 shadow-md hover:shadow-xl border border-gray-100 dark:border-gray-700 transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-indigo-100 dark:bg-indigo-900 mb-5">
                  <item.icon className="h-7 w-7 text-indigo-600 dark:text-cyan-300" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{item.title}</h3>
                <p className="text-gray-600 dark:text-gray-400">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Popular Courses
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Choose your track and build practical skills with guided projects,
              code reviews, and one-on-one support every step of the way.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {featuredCourses.map((course, index) => (
              <CourseCard key={course.id} course={course} index={index} />
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/courses"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-cyan-500 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-cyan-600 transition-all duration-200 transform hover:scale-105"
            >
              View All Courses
              <ArrowRightIcon className="h-5 w-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Learning Process */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Your 1-on-1 Learning Journey
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              A professional coaching workflow designed to keep you consistent, accountable, and improving week after week.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-6"
              >
                <div className="text-sm font-semibold text-indigo-600 dark:text-cyan-300 mb-2">Step {item.step}</div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-gray-600 dark:text-gray-400">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Professional Services
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Beyond courses, I offer comprehensive technical services including project completion, 
              technical writing, debugging, and interview preparation.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {services.slice(0, 4).map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 hover:shadow-lg transition-all duration-300"
              >
                <div className="text-center">
                  <div className="text-4xl mb-4">{service.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                    {service.description}
                  </p>
                  <div className="text-indigo-600 dark:text-cyan-300 font-semibold mb-4">
                    {service.pricing.hourly}
                  </div>
                  <Link
                    to="/services"
                    className="inline-flex items-center text-indigo-600 dark:text-cyan-300 hover:text-indigo-800 dark:hover:text-cyan-200 transition-colors duration-200"
                  >
                    Learn More
                    <ArrowRightIcon className="h-4 w-4 ml-1" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/services"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-cyan-500 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-cyan-600 transition-all duration-200 transform hover:scale-105"
            >
              View All Services
              <ArrowRightIcon className="h-5 w-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Student Success Stories
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Real feedback from learners who improved their skills, shipped projects, and advanced their careers.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredTestimonials.map((testimonial, index) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Global Reach */}
      <section className="py-20 bg-gradient-to-r from-indigo-700 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Build Skills that Open Global Opportunities
              </h2>
              <p className="text-xl mb-8 text-blue-100">
                From beginners to professionals, I have helped students across 25+ countries
                gain confidence, improve coding ability, and move toward better career outcomes.
              </p>
              <p className="text-blue-100/90 mb-6">
                If you are serious about becoming better at software development, these mentorship sessions
                are designed to move you from confusion to confidence with a proven, structured process.
              </p>
              <div className="flex flex-wrap gap-4">
                {['🇺🇸', '🇬🇧', '🇨🇦', '🇦🇺', '🇩🇪', '🇫🇷', '🇯🇵', '🇮🇳', '🇧🇷', '🇲🇽'].map((flag, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="text-3xl"
                  >
                    {flag}
                  </motion.span>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <h3 className="text-2xl font-bold mb-6">Ready to Start Your Journey?</h3>
              <p className="text-lg mb-6 text-blue-100">
                Book your session now and get a focused 30-day action plan tailored to your current level.
              </p>
              <BookingButton variant="secondary" size="large" className="w-full" label="Book Your Session Now" />
              <p className="text-sm text-blue-100 mt-4">
                Weekly capacity is limited to keep every student fully supported.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Home FAQ for Long-Tail SEO */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Answers to common questions about one-on-one mentorship sessions.
            </p>
          </motion.div>
          <div className="space-y-4">
            {homeFaqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{faq.question}</h3>
                <p className="text-gray-600 dark:text-gray-400">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <div className="fixed bottom-0 inset-x-0 z-50 p-3 bg-white/95 dark:bg-gray-900/95 border-t border-gray-200 dark:border-gray-700 shadow-2xl md:hidden">
        <BookingButton variant="primary" size="large" className="w-full" label="Book a Free Strategy Call" />
      </div>
    </div>
  );
};

export default Home;
